import React, { type JSX } from 'react';
import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMConversion,
  DOMConversionMap,
  DOMExportOutput,
  SerializedLexicalNode,
} from 'lexical';
import FileListComponent from '@/components/LexicalEditor/nodes/FileListNode/FileList';
import { RemoteFile } from '@/components/file-thumbnail/utils';

const getFileSizeFromElement = (element: HTMLElement): number | undefined => {
  const rawFileSize = element.dataset.fileSize;
  if (rawFileSize == null || rawFileSize === '') return undefined;

  const fileSize = Number(rawFileSize);
  return Number.isFinite(fileSize) ? fileSize : undefined;
};

export type ExtendedRemoteFile = RemoteFile & {
  status: 'loading' | 'error' | 'success';
  file?: File;
};

export type FileListVariant = 'default' | 'approval';

export type SerializedFileListNode = SerializedLexicalNode & {
  files: ExtendedRemoteFile[];
  variant?: FileListVariant;
};

export class FileListNode extends DecoratorNode<JSX.Element> {
  __files: ExtendedRemoteFile[];
  __variant: FileListVariant;

  static getType() {
    return 'file-list';
  }

  static clone(node: FileListNode) {
    return new FileListNode(node.__files.slice(), node.__variant, node.__key);
  }

  static importJSON(serialized: SerializedFileListNode): FileListNode {
    const key = (serialized as any).key;
    return $applyNodeReplacement(
      new FileListNode(serialized.files, serialized.variant, key),
    );
  }

  constructor(
    files: ExtendedRemoteFile[],
    variant: FileListVariant = 'default',
    key?: string,
  ) {
    super(key);
    this.__files = files;
    this.__variant = variant;
  }

  isInline(): boolean {
    return this.__variant === 'approval';
  }

  isKeyboardSelectable(): boolean {
    return false;
  }

  exportJSON(): SerializedFileListNode {
    return {
      type: 'file-list',
      version: 1,
      files: this.__files,
      variant: this.__variant,
    };
  }

  exportDOM(): DOMExportOutput {
    const isApproval = this.__variant === 'approval';
    const container = document.createElement('div');
    container.setAttribute('data-file-list', 'true');
    if (isApproval) {
      container.setAttribute('data-variant', 'approval');
    }
    this.__files.forEach((f) => {
      const a = document.createElement('a');
      const { name, link, status } = f;

      a.href = link;
      a.textContent = name;
      a.setAttribute('data-name', name);
      a.setAttribute('data-link', link);
      a.setAttribute('data-status', status);
      if (f.uuid ?? f.fileUuid) {
        a.setAttribute('data-file-uuid', f.uuid ?? f.fileUuid ?? '');
      }
      if (f.fileSize != null) {
        a.setAttribute('data-file-size', String(f.fileSize));
      }
      if (f.createdAt) {
        a.setAttribute('data-created-at', String(f.createdAt));
      }
      if (isApproval) {
        a.setAttribute('data-variant', 'approval');
      }
      container.appendChild(a);
    });
    return { element: container };
  }

  createDOM() {
    const wrapper = document.createElement('div');
    wrapper.contentEditable = 'false';
    wrapper.draggable = false;
    wrapper.style.cursor = 'default';
    wrapper.classList.add('comment-file-node');
    if (this.__variant === 'approval') {
      // approval 은 파일당 노드 1개 — 하단 margin 만 8px 로 보기 모드와 간격 일치
      wrapper.style.margin = '0 0 8px';
      // Lexical 자동 <br> 이 간격을 벌려 이 마커로 해당 br 만 CSS 제거(comment_editor.module.scss)
      wrapper.classList.add('approval-file-node');
    } else {
      // default(게시판/소셜) — 기존 동작 유지.
      wrapper.style.margin = '8px 0';
    }
    return wrapper;
  }

  updateDOM(): boolean {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (element: HTMLElement): DOMConversion<HTMLElement> | null => {
        if (
          element instanceof HTMLAnchorElement &&
          element.dataset.name &&
          element.dataset.link
        ) {
          const variant: FileListVariant =
            element.dataset.variant === 'approval' ? 'approval' : 'default';
          return {
            conversion: () => ({
              node: new FileListNode(
                [
                  {
                    link: element.getAttribute('data-link') || '',
                    name: element.getAttribute('data-name') || 'unknown',
                    status:
                      (element.getAttribute('data-status') as
                        | 'loading'
                        | 'error'
                        | 'success') ?? 'success',
                    fileUuid: element.dataset.fileUuid,
                    fileSize: getFileSizeFromElement(element),
                    createdAt: element.dataset.createdAt,
                  },
                ],
                variant,
              ),
            }),
            priority: 2,
          };
        }
        return null;
      },
    };
  }

  decorate() {
    if (this.__files.length === 0) {
      return <></>;
    }

    return <FileListComponent node={this} />;
  }
}
